import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Button,
  Card,
  Table,
  TextField,
  Badge,
  Switch,
  Code,
  IconButton,
} from '@radix-ui/themes';
import {
  ReloadIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
} from '@radix-ui/react-icons';
import AdminLayout from './AdminLayout';
import { adminApi } from '../../services/api';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useOrganization } from '../../contexts/OrganizationContext';

interface EligibilityRecord {
  cobalt_appointment_id: string;
  patient_mrn: string;
  first_name: string | null;
  last_name: string | null;
  appointment_ts: string | null;
  eligible: boolean | null;
  matched_conditions_count: number | null;
  ineligibility_reason: string | null;
  onboarded: boolean;
  automation_outcome: string | null;
  automation_detail: unknown;
  screened_at: string | null;
  patient_exists_in_phanova: boolean;
  latest_consent_status: string | null;
  has_active_care_plan: boolean;
  success: boolean;
}


const todayIso = (): string => new Date().toISOString().slice(0, 10);

const appointmentTsDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
};

const formatAppointmentTs = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};
const isoOffset = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const formatName = (r: EligibilityRecord): string => {
  const parts = [r.first_name, r.last_name].filter(Boolean) as string[];
  return parts.length > 0 ? parts.join(' ') : '—';
};

// PHA-387: schema stores raw status; UI normalizes nulls to "Not stated".
const formatConsent = (status: string | null): string => {
  if (!status) return 'Not stated';
  const lower = status.toLowerCase();
  if (lower === 'given' || lower === 'granted' || lower === 'consented') return 'Given';
  if (lower === 'rejected' || lower === 'denied' || lower === 'declined') return 'Rejected';
  return status;
};

const escapeCsv = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (records: EligibilityRecord[]) => {
  const headers = [
    'MRN',
    'Name',
    'Appointment time',
    'Eligible',
    'Outcome',
    'Onboarded',
    'Consent',
    'Success',
    'Last screened',
    'Ineligibility reason',
  ];
  const rows = records.map((r) => [
    r.patient_mrn,
    formatName(r),
    formatAppointmentTs(r.appointment_ts),
    r.eligible === null ? '' : r.eligible ? 'Yes' : 'No',
    r.automation_outcome || '',
    r.onboarded ? 'Yes' : 'No',
    formatConsent(r.latest_consent_status),
    r.success ? 'Yes' : 'No',
    r.screened_at || '',
    r.ineligibility_reason || '',
  ]);
  const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `eligibility-${todayIso()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const PAGE_SIZE = 100;

const AdminEligibilityPage: React.FC = () => {
  const navigate = useNavigate();

  // PHA-527: org-state from OrganizationContext.
  const { orgId: ctxOrgId, loading: orgLoading } = useOrganization();
  const sessionOrgId: string | null = ctxOrgId || null;
  const sessionLoaded = !orgLoading;

  const [records, setRecords] = useState<EligibilityRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [successOnly, setSuccessOnly] = useState(false);
  const [startDate, setStartDate] = useState(isoOffset(-7));
  const [endDate, setEndDate] = useState(isoOffset(30));
  const [page, setPage] = useState(1);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [exportingCsv, setExportingCsv] = useState(false);


  const loadEligibility = useCallback(async () => {
    if (!sessionOrgId || sessionOrgId === 'ALL') {
      setRecords([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.getEligibility({
        organization_id: sessionOrgId,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        search: debouncedSearch.trim() || undefined,
        success_only: successOnly,
        page,
        page_size: PAGE_SIZE,
      });
      setRecords(Array.isArray(data?.records) ? (data.records as EligibilityRecord[]) : []);
      setTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (e) {
      const err = e as Error;
      // eslint-disable-next-line no-console
      console.error('Failed to load eligibility:', err);
      setError(err.message || 'Failed to load eligibility');
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [sessionOrgId, startDate, endDate, debouncedSearch, successOnly, page]);

  useEffect(() => {
    if (sessionLoaded) loadEligibility();
  }, [sessionLoaded, loadEligibility]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, successOnly, startDate, endDate]);

  const handleOpenPatient = (mrn: string) => {
    if (!mrn) return;
    navigate(`/?openPatientId=${encodeURIComponent(mrn)}`);
  };

  // CSV export refetches with the same filters but no pagination so the
  // download includes every matching row, not just the visible page.
  const handleExportCsv = useCallback(async () => {
    if (!sessionOrgId || sessionOrgId === 'ALL') return;
    setExportingCsv(true);
    setError(null);
    try {
      const data = await adminApi.getEligibility({
        organization_id: sessionOrgId,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        search: debouncedSearch.trim() || undefined,
        success_only: successOnly,
        page: 1,
        page_size: 5000, // matches the backend cap
      });
      const allRecords = Array.isArray(data?.records)
        ? (data.records as EligibilityRecord[])
        : [];
      downloadCsv(allRecords);
    } catch (e) {
      const err = e as Error;
      // eslint-disable-next-line no-console
      console.error('CSV export failed:', err);
      setError(err.message || 'CSV export failed');
    } finally {
      setExportingCsv(false);
    }
  }, [sessionOrgId, startDate, endDate, debouncedSearch, successOnly]);

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const today = todayIso();

  return (
    <AdminLayout
      title="Eligibility"
      subtitle="Cobalt eligibility-screening results for scheduled patients."
    >
      <Card>
        <Flex direction="column" gap="3">
          <Flex gap="3" wrap="wrap" align="end">
            <Box style={{ minWidth: 240, flex: '1 1 240px' }}>
              <Text size="1" color="gray" mb="1" as="div">
                Search MRN or name
              </Text>
              <TextField.Root>
                <TextField.Input
                  placeholder="MRN / first name / last name"
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                />
              </TextField.Root>
            </Box>
            <Box>
              <Text size="1" color="gray" mb="1" as="div">
                From
              </Text>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  padding: 6,
                  borderRadius: 4,
                  border: '1px solid var(--gray-7)',
                  fontSize: 14,
                }}
              />
            </Box>
            <Box>
              <Text size="1" color="gray" mb="1" as="div">
                To
              </Text>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  padding: 6,
                  borderRadius: 4,
                  border: '1px solid var(--gray-7)',
                  fontSize: 14,
                }}
              />
            </Box>
            <Flex direction="column" gap="1">
              <Text size="1" color="gray">
                Success only
              </Text>
              <Switch checked={successOnly} onCheckedChange={setSuccessOnly} />
            </Flex>
            <Flex gap="2" ml="auto">
              <Button
                variant="soft"
                onClick={handleExportCsv}
                disabled={exportingCsv || total === 0 || !sessionOrgId || sessionOrgId === 'ALL'}
              >
                {exportingCsv ? 'Exporting…' : 'Export CSV'}
              </Button>
              <Button variant="soft" onClick={loadEligibility} disabled={loading}>
                <ReloadIcon /> Refresh
              </Button>
            </Flex>
          </Flex>

          {error && (
            <Box style={{ padding: 12, background: 'var(--red-3)', borderRadius: 4 }}>
              <Text color="red" size="2">
                {error}
              </Text>
            </Box>
          )}

          {sessionLoaded && (!sessionOrgId || sessionOrgId === 'ALL') && (
            <Box style={{ padding: 12, background: 'var(--amber-3)', borderRadius: 4 }}>
              <Text size="2">Select an organization above to view eligibility.</Text>
            </Box>
          )}

          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell width="32px"></Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>MRN</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Appointment</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Eligible</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Outcome</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Onboarded</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Consent</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Success</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Last screened</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {loading && records.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={10}>
                    <Text color="gray" size="2">
                      Loading…
                    </Text>
                  </Table.Cell>
                </Table.Row>
              )}
              {!loading && records.length === 0 && sessionOrgId && sessionOrgId !== 'ALL' && (
                <Table.Row>
                  <Table.Cell colSpan={10}>
                    <Text color="gray" size="2">
                      No eligibility records in this date range.
                    </Text>
                  </Table.Cell>
                </Table.Row>
              )}
              {records.map((r) => {
                const id = r.cobalt_appointment_id;
                const isExpanded = expanded.has(id);
                const isToday = appointmentTsDate(r.appointment_ts) === today;
                return (
                  <React.Fragment key={id}>
                    <Table.Row>
                      <Table.Cell>
                        <IconButton
                          size="1"
                          variant="ghost"
                          onClick={() => toggleExpanded(id)}
                          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                        >
                          {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                        </IconButton>
                      </Table.Cell>
                      <Table.Cell>
                        <Code variant="ghost">{r.patient_mrn}</Code>
                      </Table.Cell>
                      <Table.Cell>
                        {r.patient_exists_in_phanova ? (
                          <Button
                            variant="ghost"
                            size="1"
                            onClick={() => handleOpenPatient(r.patient_mrn)}
                          >
                            {formatName(r)} <ExternalLinkIcon />
                          </Button>
                        ) : (
                          <Text size="2" color="gray">
                            {formatName(r)}
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Flex gap="2" align="center">
                          <Text size="2">{formatAppointmentTs(r.appointment_ts)}</Text>
                          {isToday && (
                            <Badge color="blue" size="1">
                              Today
                            </Badge>
                          )}
                        </Flex>
                      </Table.Cell>
                      <Table.Cell>
                        {r.eligible === null ? (
                          <Text size="2" color="gray">
                            —
                          </Text>
                        ) : r.eligible ? (
                          <Badge color="green" size="1">
                            Eligible
                          </Badge>
                        ) : (
                          <Badge color="red" size="1">
                            Ineligible
                          </Badge>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{r.automation_outcome || '—'}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2" color={r.onboarded ? 'green' : 'gray'}>
                          {r.onboarded ? 'Yes' : 'Needs onboarding'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{formatConsent(r.latest_consent_status)}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        {r.success ? (
                          <Badge color="green" size="1">
                            ✓
                          </Badge>
                        ) : (
                          <Text size="2" color="gray">
                            —
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="1" color="gray">
                          {r.screened_at ? new Date(r.screened_at).toLocaleString() : '—'}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                    {isExpanded && (
                      <Table.Row>
                        <Table.Cell colSpan={10} style={{ background: 'var(--gray-2)' }}>
                          <Flex direction="column" gap="2" p="2">
                            {r.ineligibility_reason && (
                              <Box>
                                <Text size="1" weight="bold" color="gray" as="div">
                                  Ineligibility reason
                                </Text>
                                <Text as="div" size="2">
                                  {r.ineligibility_reason}
                                </Text>
                              </Box>
                            )}
                            <Box>
                              <Text size="1" weight="bold" color="gray" as="div">
                                Automation detail
                              </Text>
                              <pre
                                style={{
                                  margin: 0,
                                  fontSize: 12,
                                  background: 'var(--gray-3)',
                                  padding: 8,
                                  borderRadius: 4,
                                  overflow: 'auto',
                                  maxHeight: 300,
                                }}
                              >
                                {r.automation_detail
                                  ? JSON.stringify(r.automation_detail, null, 2)
                                  : '(none)'}
                              </pre>
                            </Box>
                            {r.matched_conditions_count !== null && (
                              <Text size="2" color="gray">
                                Matched conditions: {r.matched_conditions_count}
                              </Text>
                            )}
                          </Flex>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </React.Fragment>
                );
              })}
            </Table.Body>
          </Table.Root>

          <Flex gap="2" justify="end" align="center">
            <Text size="2" color="gray">
              {total === 0 ? 'No results' : `Page ${page} of ${totalPages} · ${total} total`}
            </Text>
            <Button
              variant="soft"
              size="1"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="soft"
              size="1"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Card>
    </AdminLayout>
  );
};

export default AdminEligibilityPage;
